import React, { useCallback, useEffect, useState } from "react";
import { Identifiable } from "../context";
import CrudForm, { defaultFormMessages, FormConfiguration, FormMessages } from "./form";
import CrudContainer from "./container";
import {
  default as CrudList,
  defaultListMessages,
  ListMessages,
  TableColumns,
  TableFilters,
} from "./list";
import {
  CreateActionMessages,
  DefaultTableRowActionsTooltip,
  defaultCreateActionMessages,
  defaultDeleteMessages,
  DefaultTableActions,
  DefaultTableRowActions,
  DeleteActionMessages,
} from "../actions/tableActions";
import { IAuthzAction } from "../../../lib/action-authorizer";
import { ForbiddenSection, useAuthorizedActions } from "../../../components/authorization-verifier";
import { TableActionProps, TableRowActionProps } from "../actions";
import { ErrorSchema, FormValidation, IChangeEvent } from "react-jsonschema-form";
import { Message } from "../i18n";
import { cloneDeep } from "lodash";
import { Modal } from "react-bootstrap";

export type CrudMessages = ListMessages &
  FormMessages &
  DeleteActionMessages &
  CreateActionMessages;

export const defaultCrudMessages: CrudMessages = {
  ...defaultListMessages,
  ...defaultFormMessages,
  ...defaultDeleteMessages,
  ...defaultCreateActionMessages,
};

interface SimpleCrudProps<T extends Identifiable, F> {
  onGetAll?(props: {
    filterValues?: Partial<F>;
    currentPage?: number;
    pageSize?: number;
  }): Promise<T[] | undefined>;
  onGetOne?(id: T["id"]): Promise<T>;
  onCreate?(entity: T): Promise<T["id"]> | void;
  onUpdate?(entity: T): Promise<void>;
  onDuplicate?(entity: T): Promise<void>;
  onChange?: (e: IChangeEvent<T>, es?: ErrorSchema) => T;
  updateParentForm?: (nestedFormData: T, id: string) => void;
  deleteChildForm?: (id: string) => void;
  onDelete?(id: T["id"]): Promise<void>;
  onReorder?(id: T["id"], after?: T["id"]): Promise<void>;
  disableDelete?(value: T): string | false;
  canCreate?: IAuthzAction;
  canUpdate?: IAuthzAction;
  formConfiguration: FormConfiguration<T>;
  schema: object;
  messages?: CrudMessages;
  tableColumns: TableColumns<T>;
  tableFilters?: TableFilters<T, F>;
  formContainer?: React.ComponentType<React.PropsWithChildren<{}>>;
  section?: string;
  columnVisibility?: boolean;
  rowActions?: (props: TableRowActionProps<T>) => React.ReactNode;
  additionalHeaderContent?: any;
  additionalFooterContent?: any;
  validate?: (formData: T, errors: FormValidation) => FormValidation;
  CSVRenderers?: { [key: string]: (value: any, fields: any) => string };
  listHeight?: number | string;
  paging?: boolean;
  pageSizes?: number[];
  defaultPageSize?: number;
  totalRowsCount?: number;
  columnVisibilityExtensions?: {
    columnName: string;
    togglingEnabled: boolean;
  }[];
  initialHiddenColumnNames?: string[];
  managerName?: string;
  forceLoad?: any;
  nestedData?: T[];
  nestedEntityName?: Message;
  nestedFieldId?: string;
  onExport?(props: {
    filterValues?: Partial<F>;
    currentPage?: number;
    pageSize?: number;
  }): Promise<void> | void;
  onExportText?: string;
  uncontrolledPaging?: boolean;
  fixedActions?: boolean;
  canBulkUpdate?: IAuthzAction;
  onBulkUpdate?: () => void;
  hideExportButton?: boolean;
  actions?: (props: TableActionProps<T>) => React.ReactNode;
}
export default function SimpleCrud<T extends Identifiable, F = {}>({
  messages,
  tableColumns,
  tableFilters,
  onDelete,
  disableDelete,
  onReorder,
  onDuplicate,
  onGetAll,
  onGetOne,
  onCreate,
  onUpdate,
  canCreate,
  canUpdate,
  formConfiguration,
  schema,
  formContainer,
  section,
  columnVisibility,
  rowActions,
  additionalHeaderContent,
  additionalFooterContent,
  validate,
  CSVRenderers,
  listHeight,
  paging,
  defaultPageSize,
  pageSizes,
  totalRowsCount,
  columnVisibilityExtensions = [],
  initialHiddenColumnNames,
  managerName,
  forceLoad,
  nestedData,
  updateParentForm,
  deleteChildForm,
  nestedEntityName,
  onExport,
  uncontrolledPaging,
  fixedActions,
  nestedFieldId,
  onExportText,
  canBulkUpdate,
  onBulkUpdate,
  hideExportButton,
  actions,
  onChange,
}: SimpleCrudProps<T, F>) {
  const [modalOpen, setModalOpen] = useState(false);
  const [nestedId, setNestedId] = useState();
  const [nestedMessages, setNestedMessages] = useState<CrudMessages>();
  useEffect(() => {
    if (nestedEntityName) {
      const msgs = cloneDeep(messages);
      if (msgs) {
        msgs.entityName = nestedEntityName;
        setNestedMessages(msgs);
      }
    }
  }, [messages, nestedData, nestedEntityName]);

  const handleHideModal = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  const handleOpenNestedFormModal = useCallback(
    id => {
      setNestedId(id);
      setModalOpen(true);
    },
    [setModalOpen]
  );

  const authorizedActions = useAuthorizedActions(section);
  if (!nestedData && (!authorizedActions || !authorizedActions.show)) {
    return <ForbiddenSection />;
  }
  return (
    <div id={nestedFieldId}>
      <CrudContainer>
        {{
          list: showSuccess => (
            <CrudList<T, F>
              actions={
                actions
                  ? actions
                  : props => (
                      <DefaultTableActions
                        showCreate={!!(authorizedActions.create && onCreate)}
                        showNestedCreate={!!nestedData}
                        openNestedFormModal={handleOpenNestedFormModal}
                        {...props}
                      />
                    )
              }
              rowActionsTooltip={() => (
                <DefaultTableRowActionsTooltip
                  showEdit={!!(authorizedActions.modify && onUpdate)}
                  showDelete={!!(authorizedActions.delete && onDelete)}
                  showReorder={!!(authorizedActions.modify && onReorder)}
                  showDuplicate={!!(authorizedActions.modify && onDuplicate)}
                />
              )}
              messages={nestedMessages ? nestedMessages : messages}
              filters={tableFilters}
              rowActions={
                rowActions
                  ? rowActions
                  : props => (
                      <DefaultTableRowActions
                        showReorder={!!(authorizedActions.modify && onReorder)}
                        showEdit={!!(authorizedActions.modify && onUpdate)}
                        onDelete={authorizedActions.delete ? onDelete : undefined}
                        disableDelete={disableDelete}
                        messages={messages}
                        showNestedActions={!!nestedData}
                        openNestedFormModal={handleOpenNestedFormModal}
                        deleteChildForm={deleteChildForm}
                        {...props}
                      />
                    )
              }
              columns={tableColumns}
              onReorder={authorizedActions.modify ? onReorder : undefined}
              onGetAll={onGetAll}
              showSuccessMessage={showSuccess}
              columnVisibility={columnVisibility}
              additionalHeaderContent={additionalHeaderContent}
              CSVRenderers={CSVRenderers}
              listHeight={listHeight}
              additionalFooterContent={additionalFooterContent}
              paging={paging}
              pageSizes={pageSizes}
              defaultPageSize={defaultPageSize}
              totalRowsCount={totalRowsCount}
              columnVisibilityExtensions={columnVisibilityExtensions}
              initialHiddenColumnNames={initialHiddenColumnNames}
              managerName={managerName}
              forceLoad={forceLoad}
              nestedData={nestedData}
              onExport={onExport}
              uncontrolledPaging={uncontrolledPaging}
              fixedActions={fixedActions}
              onExportText={onExportText}
              canBulkUpdate={canBulkUpdate}
              onBulkUpdate={onBulkUpdate}
              hideExportButton={hideExportButton}
            />
          ),
          create:
            authorizedActions.create && onCreate
              ? () => (
                  <CrudForm<T>
                    onGet={onGetOne}
                    formConfiguration={formConfiguration}
                    schema={schema}
                    container={formContainer}
                    onCreate={onCreate}
                    canCreate={canCreate}
                    messages={messages}
                    inSimpleCrud={true}
                    validate={validate}
                    nestedData={nestedData}
                    updateParentForm={updateParentForm}
                    customOnChange={onChange}
                  />
                )
              : undefined,
          edit:
            authorizedActions.modify && onUpdate
              ? id => (
                  <CrudForm<T>
                    onGet={onGetOne}
                    formConfiguration={formConfiguration}
                    schema={schema}
                    container={formContainer}
                    onUpdate={onUpdate}
                    canUpdate={canUpdate}
                    id={id}
                    messages={messages}
                    inSimpleCrud={true}
                    nestedData={nestedData}
                    validate={validate}
                    customOnChange={onChange}
                  />
                )
              : undefined,
        }}
      </CrudContainer>
      <Modal show={modalOpen} onHide={() => handleHideModal()} size="xl">
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <CrudForm<T>
            onGet={onGetOne}
            formConfiguration={formConfiguration}
            schema={schema}
            container={formContainer}
            onCreate={onCreate}
            canCreate={canCreate}
            onUpdate={onUpdate}
            canUpdate={canUpdate}
            id={nestedId}
            messages={nestedMessages ? nestedMessages : messages}
            inSimpleCrud={true}
            validate={validate}
            nestedData={nestedData}
            updateParentForm={updateParentForm}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
